// TODO: Assessment remote cleanup, delete this file. See "Cleanup" in docs/RemoteModules.md
import dayjs from 'dayjs';
import type { TAvailabilityCollectionPeriod } from 'types/availabilityCollectionPeriod.type';
import type { TAvailabilityCollectionSettings } from 'types/availabilityCollectionSettings.type';
import type { TAvailabilityCollectionTimeslot } from 'types/availabilityCollectionTimeslot.type';
import { z } from 'zod';

const availabilityTimeslotSchema = z
  .object({
    name: z.string().trim().min(1, { message: 'Name is required' }),
    description: z
      .string()
      .max(250, { message: 'Description must be at most 250 characters long' })
      .optional(),
    startTime: z
      .number()
      .int()
      .min(0, { message: 'Start time must be a valid number' })
      .max(1439, { message: 'Start time must be less than 24 hours' }),
    endTime: z
      .number()
      .int()
      .min(0, { message: 'End time must be a valid number' })
      .max(1439, { message: 'End time must be less than 24 hours' }),
    weekdays: z
      .array(z.number().int().min(0).max(6), {
        message: 'Weekdays must be an array of integers between 0 and 6',
      })
      .min(1, { message: 'At least one weekday must be selected' }),
  })
  .refine((data) => data.startTime < data.endTime, {
    message: 'endTime must be after startTime',
    path: ['endTime'], // attach error to `endTime` field
  });

export const validateAvailabilityTimeslot = (
  config: Partial<TAvailabilityCollectionTimeslot>
) => {
  return availabilityTimeslotSchema.safeParse(config);
};

const availabilitySettingSchema = z.object({
  reservationMode: z
    .number({ message: 'Availability reservation mode is required' })
    .int(),
  reservationTemplate: z
    .number({ message: 'Availability reservation template is required' })
    .int(),
  availabilityType: z.enum(['AVAILABILITY', 'UNAVAILABILITY'], {
    message: 'Availability type must be either AVAILABILITY or UNAVAILABILITY',
  }),
});

export const validateAvailabilitySettingSchema = (
  config: Partial<TAvailabilityCollectionSettings>
) => {
  return availabilitySettingSchema.safeParse(config);
};

const availabilityPeriodSchema = z
  .object({
    name: z.string().trim().min(1, { message: 'Name is required' }),
    description: z
      .string()
      .max(250, { message: 'Description must be at most 250 characters long' })
      .optional(),
    periodStart: z
      .string({ error: 'Start Date is required' })
      .trim()
      .min(1, { message: 'Start Date is required' }),
    periodEnd: z
      .string({ error: 'End Date is required' })
      .trim()
      .min(1, { message: 'End Date is required' }),
    timeSlots: z
      .array(z.object({}), { message: 'At least 1 timeslot is required' })
      .min(1, { message: 'At least 1 timeslot is required' }),
    periodDeadline: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const start = dayjs(data.periodStart);
    const end = dayjs(data.periodEnd);
    const deadline = data.periodDeadline ? dayjs(data.periodDeadline) : null;

    // Validate format
    if (!start.isValid()) {
      ctx.addIssue({
        path: ['periodStart'],
        code: z.ZodIssueCode.custom,
        message: 'Start date must be a valid YYYY-MM-DD date',
      });
    }
    if (!end.isValid()) {
      ctx.addIssue({
        path: ['periodEnd'],
        code: z.ZodIssueCode.custom,
        message: 'End date must be a valid YYYY-MM-DD date',
      });
    }

    // Proceed only if all are valid
    if (start.isValid() && end.isValid()) {
      if (!end.isAfter(start)) {
        ctx.addIssue({
          path: ['periodEnd'],
          code: z.ZodIssueCode.custom,
          message: 'End date must be after start date',
        });
      }

      if (deadline && deadline.isValid()) {
        if (deadline.isAfter(start)) {
          ctx.addIssue({
            path: ['periodDeadline'],
            code: z.ZodIssueCode.custom,
            message: 'Deadline must be before start date',
          });
        }
      }
    }
  });

export const validateAvailabilityPeriod = (
  config: Partial<TAvailabilityCollectionPeriod>
) => {
  return availabilityPeriodSchema.safeParse(config);
};
